import { Injectable } from '@angular/core';
import { LoginModel, Stores } from '../Models/ApplicationModels';
import { MasterDataSeriveService } from './master-data-serive.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { WeekColorService } from '../rcs/admin-module/company-config/WeekColors/services/week-color.service';
import { ColorSchedule } from '../rcs/admin-module/company-config/WeekColors/models/week-color.model';

@Injectable({
  providedIn: 'root',
})
export class CommonServiceService {
  private logoutChannel = new BroadcastChannel('logout_channel');
  public storesLoaded = false;
  public stores: Stores[] = [];
  public assignedStation = new BehaviorSubject<any>(null);
  userInfo: LoginModel = new LoginModel();
  companyApiBaseUrl: string = environment.companyApiBaseUrl;
  private readonly releaseRequestTimeoutMs = 10000;

  constructor(
    private masterDataService: MasterDataSeriveService,
    private http: HttpClient,
    private cookie: CookieService,
    private weekColorService: WeekColorService
  ) {}

  async getStores(compId: number): Promise<Stores[]> {
    if (this.storesLoaded && this.stores) {
      return this.stores;
    }

    try {
      const res = await firstValueFrom(
        this.masterDataService.getallstores(compId)
      );
      if (res) {
        this.stores = res as Stores[];
        this.storesLoaded = true;
      }
      return this.stores;
    } catch (error) {
      console.error('Failed to load stores', error);
      return [];
    }
  }

  async getWeekColorCycle(): Promise<{
    color: string;
    displayName: string;
  } | null> {
    let productionData: ColorSchedule| null = null;
    let futureData: any | null = null;
    let isOverride = localStorage.getItem('isOverride') === 'true';
    // Parse future production cycle safely
    try {
      const futureRaw = localStorage.getItem('FutureProductionCycle');
      futureData = futureRaw ? JSON.parse(futureRaw) : null;
    } catch (error) {
      console.error('Error parsing FutureProductionCycle:', error);
    }

    if (
      futureData &&
      isOverride &&
      futureData.color &&
      futureData.displayName
    ) {
      return {
        color: futureData.color,
        displayName: futureData.displayName,
      };
    }

    // Parse production cycle safely
    try {
      const prodRaw = localStorage.getItem('productionCycle');
       productionData = prodRaw ? JSON.parse(prodRaw) as ColorSchedule : null;
    } catch (error) {
      console.error('Error parsing productionCycle:', error);
    }

    // If missing, fetch from API
    if (!productionData) {
      try {
        let user = this.masterDataService.getUserDetailsFromStorage();
        const res = await firstValueFrom(
          this.weekColorService.getProductionCycleScheduleByDate(user.companyID)
        );
        if (res) {
          productionData = res;
          localStorage.setItem('productionCycle', JSON.stringify(res));
        } else {
          console.warn('Production cycle API returned empty data.');
          return null;
        }
      } catch (error) {
        console.error('Error fetching production cycle from service:', error);
        return null;
      }
    }

    const firstCycle = productionData;
    if (firstCycle?.color && firstCycle?.displayName) {
      return {
        color: firstCycle.color,
        displayName: firstCycle.displayName,
      };
    }

    return null;
  }

  unassignStationLicenseAndContinue(
    onSuccess: (res: any) => void,
    onError?: () => void
  ): void {
    this.userInfo = this.masterDataService.getUserDetailsFromStorage();
    const storedStationId = localStorage.getItem('stationId');
    const storedSessionId = localStorage.getItem('stationSessionId');
    const data = {
      assignedToUserId: Number(this.userInfo.userId),
      companyId: Number(this.userInfo.companyID),
      assignedToId: storedStationId ? Number(storedStationId) : null,
      sessionId: storedSessionId || null,
    };

    this.masterDataService
      .unassignStationLicense(data, this.userInfo.companyID)
      // Every caller of this is a logout/navigation the user is waiting on.
      // CompanyMgmt retries its TPM call three times with backoff behind a
      // 30s timeout, so without a ceiling here a degraded backend leaves
      // the user staring at a logout that appears to do nothing. On timeout
      // the error path runs, which still completes the logout locally; the
      // stale session is reclaimed by TPM's sweep.
      .pipe(timeout(this.releaseRequestTimeoutMs))
      .subscribe({
        next: (res: any) => {
          // AB#13334: clear the pointer to the session we just ended so a
          // heartbeat firing before the next assignUserStation() runs can't
          // see it reported Revoked and force a logout - callers like
          // handleStationLicenseChange() (store switch) release without
          // logging out.
          localStorage.removeItem('stationSessionId');
          onSuccess(res);
        },
        error: () => {
          if (onError) {
            onError();
          }
        },
      });
  }

  setStation(data: any) {
    this.assignedStation.next(data);
  }

  getStation(): Observable<any> {
    return this.assignedStation.asObservable();
  }
  async logoutCompany(userId: any): Promise<void> {
    const userSession = {
      userId: userId ?? null,
      sessionId: this.getSessionIdFromCookies(),
    };

    if (!userSession.userId || !userSession.sessionId) {
      return;
    }

    try {
      await firstValueFrom(this.logoutCompanyApi(userSession));
    } catch (error) {
      console.error('Error logging out company:', error);
    }
  }

  getSessionIdFromCookies(): string | null {
    const name = 'sessionId=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookies = decodedCookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(name)) {
        return cookie.substring(name.length);
      }
    }
    return null;
  }

  logoutCompanyApi(userDetails: any) {
    const url = `${this.companyApiBaseUrl}/Auth/SignOut`;
    return this.http.post(url, userDetails);
  }

  public extractErrorMessage(error: any, fallbackMessage: string): string {
    if (error?.errors) {
      return (
        Object.values(error.errors)
          .map((e: any) => e.message)
          .filter(Boolean)
          .join(', ') || fallbackMessage
      );
    }

    if (error?.error?.errors) {
      return (
        Object.values(error.error.errors)
          .map((e: any) => e.message)
          .filter(Boolean)
          .join(', ') || fallbackMessage
      );
    }

    if (error?.message) {
      return error.message;
    }

    return fallbackMessage;
  }
}
